import { useRef } from "react";
import { motion } from "framer-motion";

/* Browser window mockup wrapping a screenshot */
export const BrowserMock = ({ src, alt, domain = "neuravolt.in", className = "" }) => (
  <div className={`mock-browser ${className}`}>
    <div className="mock-bar">
      <span className="mock-dot" style={{ background: "#ff5f57" }} />
      <span className="mock-dot" style={{ background: "#febc2e" }} />
      <span className="mock-dot" style={{ background: "#28c840" }} />
      <div className="ml-3 flex-1 bg-white/5 border border-white/10 rounded-full px-3 py-0.5 flex items-center gap-1.5 max-w-[200px]">
        <span className="text-emerald-400" style={{ fontSize: "8px" }}>&#9679;</span>
        <span className="text-[10px] text-neutral-400 truncate">{domain}</span>
      </div>
    </div>
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="w-full h-full object-cover object-top"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = "/brand/launchpad.png";
      }}
    />
  </div>
);

/* Phone mockup */
export const PhoneMock = ({ src, alt, className = "" }) => (
  <div className={`mock-phone ${className}`}>
    <img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover" />
  </div>
);

/* Floating glass stat chip */
export const FloatingStat = ({ value, label, className = "", delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay, duration: 0.6 }}
    className={`rounded-2xl px-5 py-3.5 border border-white/10 ${className}`}
    style={{ background: "rgba(11,11,11,0.9)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", boxShadow: "0 20px 40px -18px rgba(0,0,0,0.5)" }}>
    <div className="font-display text-2xl font-bold text-white leading-none">{value}</div>
    <div className="text-[11px] text-neutral-400 mt-1 uppercase tracking-wider">{label}</div>
  </motion.div>
);

/* Abstract AI orb with morphing gradient + floating UI cards (replaces stock photo) */
export const AbstractOrb = () => (
  <div className="relative aspect-square max-w-[280px] sm:max-w-xs mx-auto my-4">
    <div className="morph absolute inset-4 bg-gradient-to-br from-[#FF5A1F] via-[#ff8a3d] to-[#7a2b00]" style={{ filter: "blur(2px)", opacity: 0.9 }} />
    <div className="morph absolute inset-12 bg-[#0B0B0B]/70 backdrop-blur-xl" style={{ animationDirection: "reverse" }} />
    <div className="absolute inset-0 grid place-items-center">
      <svg width="40%" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" className="opacity-90">
        <path d="M12 2a3 3 0 0 0-3 3v1a3 3 0 0 0-3 3 3 3 0 0 0 0 6 3 3 0 0 0 3 3v1a3 3 0 0 0 6 0v-1a3 3 0 0 0 3-3 3 3 0 0 0 0-6 3 3 0 0 0-3-3V5a3 3 0 0 0-3-3Z" />
        <path d="M12 8v8M8 12h8" />
      </svg>
    </div>
    
    {/* 2 Compact Floating Metrics Cards */}
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      className="glass-card rounded-xl px-3.5 py-2.5 absolute -top-3 -left-4 sm:-left-6 z-10 border border-white/50 shadow-xl backdrop-blur-md"
    >
      <div className="text-[10px] font-semibold text-[#FF5A1F] uppercase tracking-wider">Platform Uptime</div>
      <div className="font-display font-bold text-[#0B0B0B] text-base mt-0.5">99.99%</div>
    </motion.div>

    <motion.div
      animate={{ y: [0, 8, 0] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
      className="glass-card rounded-xl px-3.5 py-2.5 absolute -bottom-3 -right-4 sm:-right-6 z-10 border border-white/50 shadow-xl backdrop-blur-md"
    >
      <div className="text-[10px] font-semibold text-[#FF5A1F] uppercase tracking-wider">Infrastructure</div>
      <div className="font-display font-bold text-[#0B0B0B] text-base mt-0.5">24/7 Monitoring</div>
    </motion.div>
  </div>
);



/* Section wrapper that tracks mouse for spotlight glow */
export const Spotlight = ({ children, className = "", ...props }) => {
  const ref = useRef(null);
  const handlePos = (e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    el.style.setProperty("--mx", `${x - r.left}px`);
    el.style.setProperty("--my", `${y - r.top}px`);
  };
  return <div ref={ref} onMouseMove={handlePos} onTouchStart={handlePos} onTouchMove={handlePos} className={`spotlight ${className}`} {...props}>{children}</div>;
};
